import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { i18nTranslationHelper } from '@/i18n'
import { makeSkillWebManagerUsersRedirectUrl } from '@/main/factories/http'
import {
  findRouteByCategoryGroupHelper,
  findRouteByCategoryHelper,
  findRouteByPathnameHelper,
  getCategoryGroupRoutePathHelper,
  getCategoryRoutePathHelper,
  isInStaticRouteHelper
} from './shared/path-utils'
import type { RouteConfig } from './shared/route-types'
import { AUTH_ROUTES } from './modules/auth/paths'
import { USER_ROUTES } from './modules/user/paths'
import { BACKOFFICE_ROUTES } from './modules/backoffice/paths'

const t = i18nTranslationHelper.getTPage

// ============================================
// TIPOS (re-exportados para compatibilidade retroativa)
// ============================================

export type { RouteConfig, RoutesProps } from './shared/route-types'

// ============================================
// GRUPOS DE ROTAS COMPOSTOS A PARTIR DOS MÓDULOS
// - AUTH_ROUTES       -> públicas / não autenticadas (módulo auth)
// - USER_ROUTES       -> fluxos autenticados de usuário (módulo user)
// - BACKOFFICE_ROUTES -> fluxos autenticados administrativos (módulo backoffice)
// ============================================

const PRIVATE_ROUTES = {
  ...USER_ROUTES,
  ...BACKOFFICE_ROUTES
} as const

const PUBLIC_ROUTES = AUTH_ROUTES

// ============================================
// EXTERNAL ROUTES
// ============================================

const EXTERNAL_ROUTES = {
  MONOLITH_MANAGE_CUSTOMER: {
    name: 'monolith_manage_customers',
    value: makeSkillWebManagerUsersRedirectUrl(),
    title: t('externalRoutes.monolithManageCustomer.pageTitle')
  }
} as const

// ============================================
// WRAPPER FUNCTIONS (use helpers with app-specific routes)
// ============================================

export const findRouteByPathname = (pathname: string): RouteConfig | null => {
  return findRouteByPathnameHelper(pathname, PRIVATE_ROUTES)
}

export const findRouteByCategory = (category: Category.Enum) => {
  return findRouteByCategoryHelper(category, PRIVATE_ROUTES)
}

export const getCategoryRoutePath = (category: Category.Enum) => {
  return getCategoryRoutePathHelper(category, PRIVATE_ROUTES)
}

export const findRouteByCategoryGroup = (group: EnrollmentCategory.Enum) => {
  return findRouteByCategoryGroupHelper(group, PRIVATE_ROUTES)
}

export const getCategoryGroupRoutePath = (group: EnrollmentCategory.Enum) => {
  return getCategoryGroupRoutePathHelper(group, PRIVATE_ROUTES)
}

export const isInStaticRoute = () => {
  return isInStaticRouteHelper(PUBLIC_ROUTES)
}

// ============================================
// BACKWARD COMPATIBILITY
// Export PathConstants namespace for existing code
// ============================================

export namespace PathConstants {
  export type RouteProps = RouteConfig
  export type RoutesProps = {
    [key: string]: RouteConfig
  }
}

const ALL_ROUTES = {
  ...PRIVATE_ROUTES,
  ...PUBLIC_ROUTES,
  ...EXTERNAL_ROUTES
}

export const PathConstants = {
  Routes: ALL_ROUTES,
  findRouteByPathname,
  findRouteByCategory,
  getCategoryRoutePath,
  findRouteByCategoryGroup,
  getCategoryGroupRoutePath,
  isInStaticRoute
}

export default PathConstants
